<template>
  <div
    class="funnel-container-wrapper"
    :class="{ 'big-funnel-container-wrapper': isExpanded }"
  >
    <div class="text-overlay-container">
      <div
        v-for="(segment, index) in processedSegments"
        :key="`text-title-${segment.id}`"
        class="segment-text-external"
        :class="{ 'segment-text-external-expanded': isExpanded }"
        :style="getExternalTextStyle(segment, index)"
      >
        <div
          v-tippy="{ placement: 'top', arrow: true }"
          class="title"
          :content="getSegmentTooltip(index)"
        >
          {{ segment.title }}
        </div>
        <div class="value">{{ segment.value.toLocaleString() }}</div>
      </div>

      <div
        v-for="(segment, index) in processedSegments"
        v-if="false"
        :key="`percentage-${segment.id}`"
        class="segment-percentage-container"
        :class="{ 'segment-percentage-container-expanded': isExpanded }"
        :style="getPercentageElementStyle(segment, index)"
      >
        <div
          class="segment-percentage-text"
          :class="{ 'segment-percentage-text-expanded': isExpanded }"
        >
          {{ segment.percentage }}
        </div>
      </div>
    </div>

    <div class="funnel-svg-area">
      <svg :viewBox="viewBoxString" preserveAspectRatio="xMidYMid meet">
        <g v-for="(segment, index) in processedSegments" :key="segment.id">
          <path
            :d="calculateSegmentShadowPath(segment, index)"
            :fill="getHighlightColorWithOpacity(segment.color)"
          />
          <path
            :d="calculateSegmentPath(segment, index)"
            :fill="segment.color"
          />
        </g>
      </svg>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    initialYRatioFromTop: { type: Number, default: 0.15 },
    straightLengthRatioStart: { type: Number, default: 0.15 },
    straightLengthRatioEnd: { type: Number, default: 0.25 },
    curveStrengthFactor: { type: Number, default: 0.5 },
    minSegmentHeight: { type: Number, default: 15 },
    isExpanded: { type: Boolean, default: false },
    segmentDataInput: { type: Array, required: true },
  },
  data() {
    return {
      svgWidth: 1000,
      svgHeight: 280,
      topTextSpace: 55,
      shadowTopOverlap: 12,
      percentageElementWidth: 65,
      percentageElementHeight: 24,
      isLargeScreen: false,
      largeScreenBreakpoint: 1600,
      segmentTooltips: [
        "dashboard.funnel.tooltipVisits",
        "dashboard.funnel.tooltipImpressions",
        "dashboard.funnel.tooltipClicks",
        "dashboard.funnel.tooltipSales",
      ],
    };
  },

  computed: {
    viewBoxString() {
      const yOrigin = -this.shadowTopOverlap;
      const totalViewBoxHeight = this.svgHeight + this.shadowTopOverlap;
      return `0 ${yOrigin} ${this.svgWidth} ${totalViewBoxHeight}`;
    },
    segmentWidth() {
      return this.svgWidth / (this.segmentDataInput.length || 1);
    },
    processedSegments() {
      if (!this.segmentDataInput || this.segmentDataInput.length === 0)
        return [];
      const maxValue = this.segmentDataInput[0]?.value || 1;
      let lastCalculatedEndY = 0;

      return this.segmentDataInput.map((seg, index) => {
        const originalData = { ...seg };
        const calculatedInitialY =
          index === 0
            ? this.initialYRatioFromTop * this.svgHeight
            : lastCalculatedEndY;

        const valueRatio = Math.max(0, seg.value / maxValue);
        const firstSegmentVisibleHeight =
          this.svgHeight - this.initialYRatioFromTop * this.svgHeight;
        const targetVisibleHeight = firstSegmentVisibleHeight * valueRatio;
        let calculatedFinalEndY = this.svgHeight - targetVisibleHeight;
        calculatedFinalEndY = Math.min(
          calculatedFinalEndY,
          this.svgHeight - this.minSegmentHeight
        );
        calculatedFinalEndY = Math.max(calculatedInitialY, calculatedFinalEndY);

        lastCalculatedEndY = Math.min(
          this.svgHeight - 5,
          Math.max(5, calculatedFinalEndY)
        );
        if (lastCalculatedEndY < calculatedInitialY) {
          lastCalculatedEndY = calculatedInitialY;
        }

        const percentageValue = Math.round((seg.value / maxValue) * 100);
        const percentage = `${percentageValue}%`;

        return {
          ...originalData,
          percentage: percentage,
          calculatedInitialY: Math.min(
            this.svgHeight - 5,
            Math.max(5, calculatedInitialY)
          ),
          calculatedFinalEndYPath: lastCalculatedEndY,
          straightLengthRatioStart:
            seg.straightLengthRatioStart ?? this.straightLengthRatioStart,
          straightLengthRatioEnd:
            seg.straightLengthRatioEnd ?? this.straightLengthRatioEnd,
          curveStrengthFactor:
            seg.curveStrengthFactor ?? this.curveStrengthFactor,
        };
      });
    },
  },
  mounted() {
    this.handleResize();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.handleResize);
    }
  },
  beforeUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.handleResize);
    }
  },
  methods: {
    handleResize() {
      if (typeof window !== "undefined") {
        this.isLargeScreen = window.innerWidth >= this.largeScreenBreakpoint;
      }
    },
    getHighlightColorWithOpacity(hexColor) {
      if (
        !hexColor ||
        typeof hexColor !== "string" ||
        !hexColor.startsWith("#")
      ) {
        return "rgba(0, 0, 0, 0.1)";
      }
      let r = 0,
        g = 0,
        b = 0;
      if (hexColor.length === 4) {
        r = parseInt(hexColor[1] + hexColor[1], 16);
        g = parseInt(hexColor[2] + hexColor[2], 16);
        b = parseInt(hexColor[3] + hexColor[3], 16);
      } else if (hexColor.length === 7) {
        r = parseInt(hexColor.substring(1, 3), 16);
        g = parseInt(hexColor.substring(3, 5), 16);
        b = parseInt(hexColor.substring(5, 7), 16);
      } else {
        return "rgba(0, 0, 0, 0.1)";
      }
      if (isNaN(r) || isNaN(g) || isNaN(b)) {
        return "rgba(0,0,0,0.1)";
      }
      return `rgba(${r}, ${g}, ${b}, 0.4)`;
    },
    calculateSegmentPath(segment, index) {
      const x_seg_start = index * this.segmentWidth;
      const x_seg_end = (index + 1) * this.segmentWidth;
      const bottomYPath = this.svgHeight;

      const startY = segment.calculatedInitialY;
      const finalPathEndY = segment.calculatedFinalEndYPath;

      const straightStartLen =
        this.segmentWidth * segment.straightLengthRatioStart;
      const straightEndLen = this.segmentWidth * segment.straightLengthRatioEnd;
      const curveSectionWidth =
        this.segmentWidth - straightStartLen - straightEndLen;

      const p0x = x_seg_start;
      const p0y = startY;
      const p1x = x_seg_start + straightStartLen;
      const p1y = startY;

      const p2x = x_seg_end - straightEndLen;
      const p2y = finalPathEndY;
      const p3x = x_seg_end;
      const p3y = finalPathEndY;

      let d = `M ${p0x},${p0y} L ${p1x},${p1y} `;

      if (
        curveSectionWidth > 5 &&
        Math.abs(startY - finalPathEndY) > 0.01 * this.svgHeight
      ) {
        const handleLength = curveSectionWidth * segment.curveStrengthFactor;
        const cp1x = p1x + handleLength;
        const cp1y = p1y;
        const cp2x = p2x - handleLength;
        const cp2y = p2y;
        d += `C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2x},${p2y} `;
      } else {
        d += `L ${p2x},${p2y} `;
      }

      d += `L ${p3x},${p3y} `;
      d += `L ${x_seg_end},${bottomYPath} L ${x_seg_start},${bottomYPath} Z`;
      return d;
    },
    calculateSegmentShadowPath(segment, index) {
      const x_seg_start = index * this.segmentWidth;
      const x_seg_end = (index + 1) * this.segmentWidth;
      const topOverlap = this.shadowTopOverlap;
      const startY_shadow = segment.calculatedInitialY - topOverlap;
      const finalPathEndY_shadow = segment.calculatedFinalEndYPath - topOverlap;
      const bottomY_shadow = this.svgHeight - topOverlap;
      const straightStartLen =
        this.segmentWidth * segment.straightLengthRatioStart;
      const straightEndLen = this.segmentWidth * segment.straightLengthRatioEnd;
      const curveSectionWidth =
        this.segmentWidth - straightStartLen - straightEndLen;
      const p0x = x_seg_start;
      const p0y = startY_shadow;
      const p1x = x_seg_start + straightStartLen;
      const p1y = startY_shadow;
      const p2x = x_seg_end - straightEndLen;
      const p2y = finalPathEndY_shadow;
      const p3x = x_seg_end;
      const p3y = finalPathEndY_shadow;
      let d = `M ${p0x},${p0y} L ${p1x},${p1y} `;
      if (
        curveSectionWidth > 5 &&
        Math.abs(startY_shadow - finalPathEndY_shadow) > 0.01 * this.svgHeight
      ) {
        const handleLength = curveSectionWidth * segment.curveStrengthFactor;
        const cp1x = p1x + handleLength;
        const cp1y = p1y;
        const cp2x = p2x - handleLength;
        const cp2y = p2y;
        d += `C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2x},${p2y} `;
      } else {
        d += `L ${p2x},${p2y} `;
      }
      d += `L ${p3x},${p3y} `;
      d += `L ${x_seg_end},${bottomY_shadow} L ${x_seg_start},${bottomY_shadow} Z`;
      return d;
    },

    getPercentageElementStyle(segment, index) {
      const numSegments = this.segmentDataInput.length || 1;
      const segWidthPercentage = 100 / numSegments;

      let style = {
        position: "absolute",
      };

      const externalTextCurrentTop = this.isExpanded ? -25 : -60;
      const externalTextHeight = this.topTextSpace - 15;

      let gapBelowExternalText;
      if (this.isExpanded) {
        gapBelowExternalText = this.isLargeScreen ? 20 : 15;
      } else {
        gapBelowExternalText = this.isLargeScreen ? 10 : 5;
      }
      style.top = `${
        externalTextCurrentTop + externalTextHeight + gapBelowExternalText
      }px`;

      if (this.isExpanded) {
        const segmentRightEdgeContainerPercentage =
          (index + 1) * segWidthPercentage;
        style.right = `${100 - segmentRightEdgeContainerPercentage}%`;
      } else {
        const segmentCenterLeftPercentage =
          index * segWidthPercentage + segWidthPercentage / 2;
        style.left = `${segmentCenterLeftPercentage}%`;
        style.transform = "translateX(-50%)";
      }

      return style;
    },

    getExternalTextStyle(segment, index) {
      const numSegments = this.segmentDataInput.length || 1;
      const segWidthPercentage = 100 / numSegments;
      const segmentCenterLeftPercentage =
        index * segWidthPercentage + segWidthPercentage / 2;

      let style = {
        position: "absolute",
        height: `${this.topTextSpace - 15}px`,
        left: `${segmentCenterLeftPercentage}%`,
        transform: "translateX(-50%)",
      };

      if (this.isExpanded) {
        style.top = "-85px";
      } else {
        style.top = "-60px";
        style.width = `${this.segmentWidth}px`;
      }

      return style;
    },
    getSegmentTooltip(index) {
      if (index < this.segmentTooltips.length) {
        return this.$t(this.segmentTooltips[index]);
      }
      return this.$t("dashboard.funnel.tooltipVisits");
    },
  },
};
</script>

<style scoped lang="scss">
.funnel-container-wrapper {
  position: relative;
  width: 100%;
  max-width: 90%;
  margin: 0 15px;
}
.big-funnel-container-wrapper {
  margin: 0 auto;
}
.text-overlay-container {
  position: absolute;
  top: 100px;
  left: 0;
  width: 100%;
  pointer-events: none;
  z-index: 1;
}

.segment-text-external {
  text-align: center;
  box-sizing: border-box;
  pointer-events: auto;
  top: -60px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.segment-text-external .title {
  @include base-font;
  @include font-smaller;
  color: black;
  font-weight: 500;
  margin-bottom: 3px;
  white-space: wrap;
  text-overflow: ellipsis;
  width: 120px;
}

.segment-text-external .value {
  @include base-font;
  @include font-smaller;
  font-weight: 800;
  color: black;
  white-space: nowrap;
}

.segment-text-external-expanded {
  align-items: center;
  top: -70px;
  margin-right: 16px;
}

.segment-text-external-expanded .title {
  @include font-big;
  white-space: wrap;
  width: 180px;
  margin-bottom: 16px;
}

.segment-text-external-expanded .value {
  font-size: 24px;
}

.segment-percentage-container {
  height: 16px;
  width: 38px;
  padding: 2px 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  border-radius: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  pointer-events: auto;
}

.segment-percentage-container-expanded {
  height: 20px;
  width: 48px;
  margin-right: 16px;
}

.segment-percentage-text {
  @include base-font;
  @include font-smaller;
  font-weight: 600;
  white-space: nowrap;
}

.segment-percentage-text-expanded {
  @include font-normal;
}

.funnel-svg-area {
  overflow: hidden;
}

svg {
  display: block;
  width: 100%;
  height: auto;
}
</style>
